import {
  DetalleOperacion,
  RegistroEstado,
  ResultadoCesionDocumentosDetalle,
} from '@/models/respuestas/detalleOperacion.model';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ACTIVO, CARGANDO, ERROR, INACTIVO } from '../constantes';
import {
  DETALLES_OPERACION_CLIENTE,
  DETALLES_OPERACION_EJECUTIVO,
  OBTENER_FERIADOS,
  PARAMETROS_SIMULACIONES,
} from '@/helpers/urls';
import api from '@/providers/api';
import RespuestaApi from '@/models/respuestas/resApi.model';
import { mostrarAlertaError } from '@/helpers/alert';
import { differenceInCalendarDays } from 'date-fns';
import { formatDate, parseDate } from '@/helpers/date';
import { Feriado } from '@/models/respuestas/feriado.model';
import {
  DetalleOpStore,
  DocDetalleOpStore,
} from '@/models/store/detalleOperacionStore.model';
import { ResParametros } from '@/models/respuestas/parametroSim.model';
import { parametroSimAdapter } from '@/models/adapters/parametroSim.adapter';
import { operacionEstaGuardada } from '@/helpers/estadosOperacion';

// Respuesta devuelta cuando se cambia el estado de una operacion a cedida
export interface RespuestaCesion {
  documentosCedidos: ResultadoCesionDocumentosDetalle[];
  documentosNoCedidos: ResultadoCesionDocumentosDetalle[];
  registroEstado: RegistroEstado;
}

type DiccionarioResultadoCesion = Record<
  ResultadoCesionDocumentosDetalle['idDocumento'],
  ResultadoCesionDocumentosDetalle
>;

type ObtenerDetalleParams = {
  idOperacion: number;
  idRol: number;
};

type PayloadEdicion = {
  folio: string;
  nuevoVencimiento: Date;
};

export const obtenerDetalleOperacion = createAsyncThunk(
  'detalleOperacion/obtener',
  async (info: ObtenerDetalleParams): Promise<DetalleOpStore> => {
    try {
      //#region obtencion detalle
      const {
        data: { resultado: operacion },
      } = await api.get<RespuestaApi<DetalleOperacion>>(
        info.idRol === 1
          ? DETALLES_OPERACION_CLIENTE(info.idOperacion)
          : DETALLES_OPERACION_EJECUTIVO(info.idOperacion)
      );

      // Obtengo los parametros del cliente
      const {
        data: {
          resultado: { cantidadOperacionesGiradas, parametros },
        },
      } = await api.get<RespuestaApi<ResParametros>>(
        PARAMETROS_SIMULACIONES(operacion.rutCliente)
      );

      const estadoOperacion = operacion?.registrosEstado.at(-1).idEstado;

      let documentos: DocDetalleOpStore[];
      documentos = operacion.documentos as DocDetalleOpStore[];
      if (!operacionEstaGuardada(estadoOperacion)) {
        documentos = operacion.documentos.map((d) => {
          {/* Comentado porque se desahibilita recalculo de montos, los dias de plazo se guardan al registrar operacion. */}
/*           const diasPlazo = differenceInCalendarDays(
            parseDate(d.fechaVencimiento),
            new Date()
          ); */

          return { ...d, ediciones: [] };
        });
      } else {
        documentos = operacion.documentos as DocDetalleOpStore[];
      }
      //#endregion

      return {
        ...operacion,
        estado: estadoOperacion,
        documentos,
        backupDocs: documentos,
        cantidadOperacionesGiradas,
        parametros: parametroSimAdapter(parametros),
        edicion: false,
      };
    } catch (err) {
      mostrarAlertaError('Hubo un error al obtener la operacion');
      throw err;
    }
  }
);

export const obtenerFeriados = createAsyncThunk(
  'detalleOperacion/feriados',
  async () => {
    try {
      const {
        data: { resultado: feriados },
      } = await api.get<RespuestaApi<Feriado[]>>(OBTENER_FERIADOS);

      return feriados.map((f) => f.fecha);
    } catch (err) {
      mostrarAlertaError('Hubo un error al obtener los feriados');
      throw err;
    }
  }
);

export const estadoInicialDetalleOperacion = {
  operacion: null as DetalleOpStore,
  feriados: null as string[],
  estado: INACTIVO,
};

const detalleOperacionSlice = createSlice({
  name: 'detalleOperacion',
  initialState: estadoInicialDetalleOperacion,
  reducers: {
    autorizarCesion: ({ operacion }) => {
      operacion.autorizaCeder = true;
    },
    actualizarEstado: (
      { operacion },
      { payload }: PayloadAction<RegistroEstado>
    ) => {
      operacion.registrosEstado.push(payload);
      operacion.estado = payload.idEstado;
    },
    actualizarEstadoCesion: (
      { operacion },
      { payload: p }: PayloadAction<RespuestaCesion>
    ) => {
      // Creo un objeto de la informacion con el estado de cesion obtenido por cada documento
      const estados: DiccionarioResultadoCesion = [
        ...p.documentosCedidos,
        ...p.documentosNoCedidos,
      ].reduce((acc, curr) => {
        acc[curr.idDocumento] = curr;

        return acc;
      }, {} as DiccionarioResultadoCesion);

      if (Object.keys(estados)?.length > 0) {
        // Actualizo el vencimiento y el estado de cesion de los documentos
        operacion.documentos.forEach((d, i) => {
          const folio = Number(d.idDocumento);
          const info = estados[folio];

          operacion.documentos[i] = {
            ...d,
            ...info,
          };
        });
      }

      if (p.registroEstado) {
        // Agrego el nuevo estado
        operacion.estado = p.registroEstado.idEstado;
        operacion.registrosEstado.push(p.registroEstado);
      }
    },
    reestablecerCambios: ({ operacion }, { payload }) => {
      if (payload) {
        const indice = operacion.documentos.findIndex(
          (d) => d.nroFolio === payload
        );
        if (indice !== -1) {
          const backup = operacion.backupDocs.find(
            (d) => d.nroFolio === payload
          );
          if (backup) {
            operacion.documentos[indice] = { ...backup };
            operacion.edicion = false;
            return; // Salimos después de actualizar el documento específico
          }
        }
      }

      // Si no se encontró el documento o no se proporcionó un payload,
      // restablecemos todos los documentos y actualizamos el estado de edición.
      operacion.documentos = [...operacion.backupDocs];
      operacion.edicion = false;
    },
    modificarPlazoFactura: (
      { operacion: { documentos, backupDocs } },
      { payload }: PayloadAction<PayloadEdicion>
    ) => {
      const nuevoPlazo = differenceInCalendarDays(
        payload.nuevoVencimiento,
        new Date()
      );

      const i = documentos.findIndex((d) => d.nroFolio === payload.folio);

      // Actualizo el documento
      documentos[i].fechaVencimiento = formatDate(payload.nuevoVencimiento);
      documentos[i].diasPlazo = nuevoPlazo;

      if (backupDocs[i].diasPlazo === nuevoPlazo) {
        // Si el plazo introducido es el que esta guardado en la BD, desactivo el modo de edicion
        documentos[i].ediciones = documentos[i].ediciones.filter(
          (e) => e !== 'plazo'
        );
      } else {
        documentos[i].ediciones.push('plazo');
      }
    },
    alternarFactura: (
      { operacion: { documentos, backupDocs } },
      { payload }: PayloadAction<DocDetalleOpStore['nroFolio']>
    ) => {
      const i = documentos.findIndex((d) => d.nroFolio === payload);

      // Invierto el estado
      const nuevoValor = !documentos[i].estado;
      documentos[i].estado = !documentos[i].estado;

      if (backupDocs[i].estado === nuevoValor) {
        documentos[i].ediciones = documentos[i].ediciones.filter(
          (e) => e !== 'estado'
        );
      } else {
        documentos[i].ediciones.push('estado');
      }
    },
    guardarModificacionesOperacion: ({ operacion }) => {
      operacion.documentos.forEach(
        (d, i) => (operacion.documentos[i] = { ...d, ediciones: [] })
      );
      operacion.backupDocs = operacion.documentos;
      operacion.edicion = false;
    },
    guardarSimulacionActualizada: (
      state,
      { payload }: PayloadAction<DetalleOpStore>
    ) =>
      // Actualizacion de los montos cuando se establece la operacion en girada o rechazada
      // Para reemplazar los datos desactualizados obtenidos desde la bd (a nivel visual)
      {
        if (payload) {
          state.operacion = {
            ...payload,
            // Agrego propiedades para que llegue data desactualizada
            documentos: state.operacion.documentos,
            backupDocs: state.operacion.backupDocs,
            edicion: state.operacion.edicion,
            registrosEstado: state.operacion.registrosEstado,
          };
        }
      },

    manejarModoEdicion: ({ operacion }) => {
      const hayDocsModificados = operacion.documentos.some(
        (d) => d.ediciones.length > 0
      );

      if (hayDocsModificados) {
        operacion.edicion = true;
        return;
      }
      operacion.edicion = false;
    },

    limpiarDetalleOperacion: (state) => {
      return { ...state, ...estadoInicialDetalleOperacion };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(obtenerDetalleOperacion.pending, (state) => {
        state.estado = CARGANDO;
      })
      .addCase(obtenerDetalleOperacion.fulfilled, (state, { payload }) => {
        state.estado = ACTIVO;
        state.operacion = payload;
      })
      .addCase(obtenerFeriados.fulfilled, (state, { payload }) => {
        state.feriados = payload;
      })
      .addCase(obtenerDetalleOperacion.rejected, (state) => {
        state.estado = ERROR;
      })
      .addMatcher(
        (action) => {
          const {
            guardarSimulacionActualizada,
            modificarPlazoFactura,
            alternarFactura,
          } = detalleOperacionSlice.actions;

          return (
            action.type === guardarSimulacionActualizada.type ||
            action.type === modificarPlazoFactura.type ||
            action.type === alternarFactura.type
          );
        },
        (state) => {
          state.operacion &&
            detalleOperacionSlice.caseReducers.manejarModoEdicion(state);
        }
      );
  },
});

export const {
  actualizarEstado,
  actualizarEstadoCesion,
  autorizarCesion,
  modificarPlazoFactura,
  reestablecerCambios,
  guardarModificacionesOperacion,
  alternarFactura,
  guardarSimulacionActualizada,
  limpiarDetalleOperacion,
} = detalleOperacionSlice.actions;

export default detalleOperacionSlice.reducer;
